<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    count?: number | string
    overflowCount?: number
    dot?: boolean
    status?: 'success' | 'processing' | 'default' | 'error' | 'warning'
    text?: string
    color?: string
  }>(),
  {
    count: undefined,
    overflowCount: 99,
    dot: false,
    status: undefined,
    text: '',
    color: undefined
  }
)

const isStatusMode = computed(() => !!props.status || !!props.color)

const displayCount = computed(() => {
  if (typeof props.count === 'number' && props.count > props.overflowCount) {
    return `${props.overflowCount}+`
  }
  return props.count
})

const showBadge = computed(() => {
  if (props.dot) return true
  if (props.count !== undefined && props.count !== null && props.count !== '') {
    return typeof props.count === 'number' ? props.count > 0 : true
  }
  return false
})
</script>

<template>
  <span class="rr-badge" :class="{ 'rr-badge--status': isStatusMode }">
    <template v-if="isStatusMode">
      <span
        class="rr-badge__status-dot"
        :class="status ? `is-${status}` : undefined"
        :style="color ? { backgroundColor: color } : undefined"
      />
      <span v-if="text || $slots.default" class="rr-badge__status-text">
        <slot>{{ text }}</slot>
      </span>
    </template>
    <template v-else>
      <slot />
      <sup
        v-if="showBadge"
        class="rr-badge__count"
        :class="{ 'is-dot': dot }"
      >
        {{ !dot ? displayCount : '' }}
      </sup>
    </template>
  </span>
</template>

<style scoped>
.rr-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  vertical-align: middle;
}

.rr-badge__count {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  transform-origin: 100% 0;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 9px;
  background: var(--rr-color-danger);
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 0 0 1.5px var(--rr-color-surface);
  white-space: nowrap;
}

.rr-badge__count.is-dot {
  width: 7px;
  min-width: 7px;
  height: 7px;
  padding: 0;
  border-radius: 50%;
}

.rr-badge--status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--rr-color-text);
}

.rr-badge__status-dot {
  position: relative;
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rr-badge__status-dot.is-success { background: var(--rr-color-success); }
.rr-badge__status-dot.is-error { background: var(--rr-color-danger); }
.rr-badge__status-dot.is-warning { background: var(--rr-color-warning); }
.rr-badge__status-dot.is-default { background: var(--rr-color-text-subtle); }
.rr-badge__status-dot.is-processing {
  background: var(--rr-color-info);
}
.rr-badge__status-dot.is-processing::after {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 1px solid var(--rr-color-info);
  border-radius: 50%;
  animation: rr-badge-status-processing 1.6s infinite ease-in-out;
  content: "";
}

@keyframes rr-badge-status-processing {
  0% { transform: scale(0.9); opacity: 0.6; }
  100% { transform: scale(2.2); opacity: 0; }
}
</style>
